/**
 * Gerador canônico de `normalizedKey` (`autor_ano`).
 *
 * Módulo neutro e agnóstico de provedor: produz a chave normalizada que
 * identifica uma obra por primeiro-autor + ano. Extraído byte-equivalente de
 * `nucleoBibliograficoService.ts` (`generateNormalizedKey:89` / `normalizeAuthor:75`,
 * lado leitor / Onda 1) para que o produtor Crossref (Horizonte Onda 2) gere
 * chaves idênticas às da Biblioteca — invariante travado por
 * `normalizedKey.test.ts` (AC-09, identidade cross-fonte).
 *
 * As cópias originais em `nucleoBibliograficoService.ts:89` e
 * `nucleoExtractionService.ts:66` permanecem intactas nesta etapa; unificá-las
 * para consumir este módulo é follow-up registrado (Sprint 035, fora de escopo).
 */

/**
 * Normaliza um autor para o componente de sobrenome da chave.
 * Guarda de null/tipo: autor faltando/inválido → `unknown`.
 */
function normalizeAuthor(author: string | null | undefined): string {
  if (!author || typeof author !== 'string') return 'unknown';
  const cleaned = author.trim().toLowerCase();
  if (cleaned.includes(',')) {
    return cleaned.split(',')[0].trim().replace(/[^a-záàâãéèêíïóôõöúçñ]/g, '');
  }
  const parts = cleaned.split(/\s+/);
  return parts[parts.length - 1].replace(/[^a-záàâãéèêíïóôõöúçñ]/g, '');
}

/**
 * Gera a chave normalizada `<sobrenome>_<ano>` para uma obra.
 * Guarda de null: autores faltando/inválidos → `unknown_<ano|nd>`.
 */
export function generateNormalizedKey(
  authors: string[] | null | undefined,
  year: number | null,
): string {
  if (!authors || !Array.isArray(authors) || authors.length === 0) {
    return `unknown_${year || 'nd'}`;
  }
  const firstAuthor = normalizeAuthor(authors[0]);
  return `${firstAuthor}_${year || 'nd'}`;
}
